import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { LegacyLeafletScriptLoaderService } from './legacy-leaflet-script-loader.service';

type DeferredStylesheetManifest = Partial<Record<'admin-shell' | 'map-shell', string>>;

@Injectable({
    providedIn: 'root'
})
export class DeferredStylesheetLoaderService {
    private readonly manifestUrl = '/assets/boot/rtls-style-manifest.json';
    private readonly useManifest = environment.production;
    private readonly publicLandingPrefixes = ['/', '/landing'];
    private readonly publicMapPrefixes = ['/', '/landing'];
    private readonly loadedStylesheets = new Set<string>();
    private readonly pendingStylesheets = new Map<string, Promise<void>>();
    private manifestPromise?: Promise<DeferredStylesheetManifest>;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly legacyLeafletScriptLoader: LegacyLeafletScriptLoaderService
    ) { }

    ensureForPath(url: string): void {
        const path = this.normalizePath(url);

        if (this.shouldLoadAdminShell(path)) {
            void this.ensureStylesheet('admin-shell');
        }

        if (this.shouldLoadMapShell(path)) {
            void this.ensureStylesheet('map-shell');
        }
    }

    private shouldLoadAdminShell(path: string): boolean {
        return !this.publicLandingPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    }

    private shouldLoadMapShell(path: string): boolean {
        if (this.publicMapPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
            return true;
        }

        return this.legacyLeafletScriptLoader.shouldLoadForPath(path);
    }

    private async ensureStylesheet(key: 'admin-shell' | 'map-shell'): Promise<void> {
        if (this.loadedStylesheets.has(key)) {
            return;
        }

        const existingPromise = this.pendingStylesheets.get(key);
        if (existingPromise) {
            await existingPromise;
            return;
        }

        const pending = this.loadStylesheet(key)
            .finally(() => {
                this.pendingStylesheets.delete(key);
            });

        this.pendingStylesheets.set(key, pending);
        await pending;
    }

    private async loadStylesheet(key: 'admin-shell' | 'map-shell'): Promise<void> {
        const href = await this.resolveStylesheetHref(key);
        if (!href) {
            return;
        }

        if (this.hasManagedStylesheet(key) || this.hasStylesheetHref(href)) {
            this.loadedStylesheets.add(key);
            return;
        }

        await new Promise<void>((resolve) => {
            const link = this.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.dataset.rtlsDeferredStylesheet = key;
            link.onload = () => {
                this.loadedStylesheets.add(key);
                resolve();
            };
            link.onerror = () => {
                link.remove();
                resolve();
            };
            this.document.head.appendChild(link);
        });
    }

    private async resolveStylesheetHref(key: 'admin-shell' | 'map-shell'): Promise<string> {
        if (!this.useManifest) {
            return `/${key}.css`;
        }

        const manifest = await this.getManifest();
        const candidates = [
            manifest[key],
            `${key}.css`,
            `/${key}.css`
        ].filter((value): value is string => !!value);

        return candidates[0] ? new URL(candidates[0], this.document.baseURI).toString() : '';
    }

    private async getManifest(): Promise<DeferredStylesheetManifest> {
        if (!this.useManifest) {
            return {};
        }

        if (!this.manifestPromise) {
            this.manifestPromise = fetch(new URL(this.manifestUrl, this.document.baseURI).toString(), {
                credentials: 'same-origin'
            })
                .then(async (response) => {
                    if (!response.ok) {
                        return {};
                    }

                    return response.json() as Promise<DeferredStylesheetManifest>;
                })
                .catch(() => ({}));
        }

        return this.manifestPromise;
    }

    private hasManagedStylesheet(key: string): boolean {
        return !!this.document.head.querySelector(`link[data-rtls-deferred-stylesheet="${key}"]`);
    }

    private hasStylesheetHref(href: string): boolean {
        return Array.from(this.document.styleSheets).some((sheet) => {
            const currentHref = (sheet.href || '').toString();
            return currentHref === href;
        });
    }

    private normalizePath(url: string): string {
        const source = (url || '').trim();
        const normalized = source.split('?')[0].split('#')[0].trim();
        return normalized || '/';
    }
}
